import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { hideSpinner, showSpinner } from 'src/app/core/services/functions.service';
import { HelpersService } from 'src/app/core/services/helpers.service';
import {
  GestionarOpsClientesService,
  OpItem,
  ClienteItem,
  TipoVehiculoItem,
  HistoricoEnvioItem,
  HistoricoEnvioResumenGrupo,
} from './gestionar-ops-clientes.service';
import { ModalHistoricoEnvioDetalleComponent } from '../modal-historico-envio-detalle/modal-historico-envio-detalle.component';

interface ResumenGrupoParsed {
  fechaEnvio: string;
  url: string;
  statusCode: string;
  repeticiones: number;
  success: boolean;
  status: string;
  added: string[];
  edited: string[];
  skipped: string[];
  parseError: boolean;
}

@Component({
  selector: 'app-gestionar-ops-clientes',
  templateUrl: './gestionar-ops-clientes.component.html',
  styleUrls: ['./gestionar-ops-clientes.component.scss'],
})
export class GestionarOpsClientesComponent implements OnInit {
  @ViewChild('paginatorOps') paginator: MatPaginator;
  @ViewChild('sortOps') sort: MatSort;

  @ViewChild('paginatorHistorico') set paginatorHistorico(mp: MatPaginator) {
    if (mp) this.historicoDataSource.paginator = mp;
  }
  @ViewChild('sortHistorico') set sortHistorico(ms: MatSort) {
    if (ms) this.historicoDataSource.sort = ms;
  }

  transportes: string[] = [];
  clientes: ClienteItem[] = [];
  tiposVehiculo: TipoVehiculoItem[] = [];
  dataSource = new MatTableDataSource<OpItem>([]);
  busqueda = new FormControl('');

  transporteSeleccionado = new FormControl([]);
  clienteSeleccionado = new FormControl('');
  tipoVehiculoSeleccionado = new FormControl('');

  busquedaTransporte = new FormControl('');
  busquedaCliente = new FormControl('');
  busquedaTipoVehiculo = new FormControl('');

  cargandoTransportes = false;
  cargandoTiposVehiculo = false;
  cargandoTabla = false;
  errorTabla = '';

  columnas: { name: keyof OpItem; title: string }[] = [
    { name: 'TRANSPORTE', title: 'Transporte' },
    { name: 'CLASE_TRANSPORTE', title: 'Clase Transporte' },
    { name: 'FECHA_PICKING', title: 'Fecha Picking' },
    { name: 'NUM_PEDIDO_SAP', title: 'Pedido SAP' },
    { name: 'NUM_ENTREGA_SAP', title: 'Entrega SAP' },
    { name: 'CLIENTE', title: 'Transportadora' },
    { name: 'TIPO_VEHICULO', title: 'Tipo Vehículo' },
  ];

  displayedColumns = this.columnas.map((c) => c.name);

  // ─── Histórico de envío ───────────────────────────────────────────────────
  modoFechaHistorico = new FormControl('especifica');
  fechaEspecificaHistorico = new FormControl(new Date());
  fechaInicioRangoHistorico = new FormControl(this.restarDias(new Date(), 6));
  fechaFinRangoHistorico = new FormControl(new Date());
  busquedaHistorico = new FormControl('');
  filtroReintentoHistorico = new FormControl('sin_reintento');

  historicoDataSource = new MatTableDataSource<HistoricoEnvioItem>([]);
  cargandoHistorico = false;
  errorHistorico = '';
  historicoConsultado = false;

  resumenHistoricoGrupos: ResumenGrupoParsed[] = [];
  cargandoResumenHistorico = false;

  columnasHistorico: { name: keyof HistoricoEnvioItem; title: string }[] = [
    { name: 'NUM_PEDIDO_SEUS', title: 'Pedido SEUS' },
    { name: 'NUM_ENTREGA_SAP', title: 'Entrega SAP' },
    { name: 'CATEGORY', title: 'Categoría' },
    { name: 'DETALLE', title: 'Detalle' },
    { name: 'STATUS', title: 'Estado' },
    { name: 'REINTENTO', title: 'Reintento' },
    { name: 'FECHA_ENVIO', title: 'Fecha Envío' },
    { name: 'USUARIO_CREACION', title: 'Usuario' },
  ];

  displayedColumnsHistorico = [...this.columnasHistorico.map((c) => c.name), 'ACCIONES'];

  constructor(
    private service: GestionarOpsClientesService,
    private helpers: HelpersService,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    this.helpers.setTitle('Gestionar OPs Clientes');
    await Promise.all([
      this.cargarTransportes(),
      this.cargarOps(),
      this.cargarClientes(),
      this.cargarTiposVehiculo(),
    ]);

    this.busqueda.valueChanges.subscribe((value) => {
      this.dataSource.filter = (value || '').trim().toLowerCase();
    });

    this.busquedaHistorico.valueChanges.subscribe((value) => {
      this.historicoDataSource.filter = (value || '').trim().toLowerCase();
    });

    this.fechaInicioRangoHistorico.valueChanges.subscribe((inicio: Date) => {
      if (!inicio) return;
      const fin: Date = this.fechaFinRangoHistorico.value;
      const max = this.sumarDias(inicio, 6);
      if (fin && fin < inicio) {
        this.fechaFinRangoHistorico.setValue(inicio);
      } else if (fin && fin > max) {
        this.fechaFinRangoHistorico.setValue(max);
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  private async cargarTransportes(): Promise<void> {
    this.cargandoTransportes = true;
    const result = await this.service.getTransportes();
    this.cargandoTransportes = false;
    if (result.success) {
      this.transportes = result.data;
    } else {
      this.helpers.notifications.errorMessage(result.message || 'Error al cargar transportes');
    }
  }

  private async cargarClientes(): Promise<void> {
    const result = await this.service.getClientes();
    if (result.success) {
      this.clientes = result.data;
    } else {
      this.helpers.notifications.errorMessage(result.message || 'Error al cargar clientes');
    }
  }

  private async cargarTiposVehiculo(): Promise<void> {
    this.cargandoTiposVehiculo = true;
    const result = await this.service.getTiposVehiculo();
    this.cargandoTiposVehiculo = false;
    if (result.success) {
      this.tiposVehiculo = result.data;
    } else {
      this.helpers.notifications.errorMessage(result.message || 'Error al cargar tipos de vehículo');
    }
  }

  async cargarOps(): Promise<void> {
    this.cargandoTabla = true;
    this.errorTabla = '';
    const result = await this.service.getOps();
    this.cargandoTabla = false;
    if (result.success) {
      this.dataSource.data = result.data;
    } else {
      this.errorTabla = result.message || 'Error al cargar OPs';
    }
  }

  async actualizarCliente(): Promise<void> {
    const transportes: string[] = this.transporteSeleccionado.value || [];
    const clienteCodigo = this.clienteSeleccionado.value;
    const tipoVehiculo: string = this.tipoVehiculoSeleccionado.value;

    if (!transportes.length) {
      this.helpers.notifications.warningMessage('Selecciona al menos un transporte antes de continuar.');
      return;
    }
    if (!clienteCodigo) {
      this.helpers.notifications.warningMessage('Selecciona un cliente antes de continuar.');
      return;
    }

    const clienteNombre =
      this.clientes.find((c) => c.codigo === clienteCodigo)?.nombre || clienteCodigo;
    const detalleTransportes =
      transportes.length === 1 ? transportes[0] : `${transportes.length} transportes`;
    const detalleVehiculo = tipoVehiculo ? ` y el tipo de vehículo a <b>${tipoVehiculo}</b>` : '';

    this.helpers.notifications.warningDialog(
      'Confirmar actualización',
      `¿Actualizar el cliente a <b>${clienteNombre}</b>${detalleVehiculo} para <b>${detalleTransportes}</b>?`,
      'Aceptar',
      async () => {
        showSpinner('Actualizando cliente...');
        const result = await this.service.actualizarCliente(transportes, clienteCodigo, tipoVehiculo);
        hideSpinner();
        if (result.success) {
          this.helpers.notifications.successMessage(
            `${result.message} — ${result.registros_actualizados} registro(s) actualizado(s).`
          );
          await this.cargarOps();
        } else {
          this.helpers.notifications.errorMessage(result.message);
        }
      },
      '480px'
    );
  }

  async blanquearCliente(): Promise<void> {
    const transportes: string[] = this.transporteSeleccionado.value || [];
    if (!transportes.length) {
      this.helpers.notifications.warningMessage('Selecciona al menos un transporte para blanquear el cliente.');
      return;
    }

    const detalleTransportes =
      transportes.length === 1 ? transportes[0] : `${transportes.length} transportes`;

    this.helpers.notifications.warningDialog(
      'Confirmar blanqueo',
      `¿Blanquear el cliente de <b>${detalleTransportes}</b>? Esta acción limpiará el campo en todos los registros asociados.`,
      'Blanquear',
      async () => {
        showSpinner('Blanqueando cliente...');
        const result = await this.service.blanquearCliente(transportes);
        hideSpinner();
        if (result.success) {
          this.helpers.notifications.successMessage(
            `${result.message} — ${result.registros_actualizados} registro(s) actualizados.`
          );
          await this.cargarOps();
        } else {
          this.helpers.notifications.errorMessage(result.message);
        }
      },
      '480px'
    );
  }

  formatearFecha(fecha: string | null): string {
    if (!fecha || fecha.length !== 8) return fecha || '-';
    return `${fecha.slice(6, 8)}/${fecha.slice(4, 6)}/${fecha.slice(0, 4)}`;
  }

  get transportesFiltrados(): string[] {
    const q = (this.busquedaTransporte.value || '').toLowerCase();
    return q ? this.transportes.filter((t) => t.toLowerCase().includes(q)) : this.transportes;
  }

  get todosTransportesFiltradosSeleccionados(): boolean {
    const filtrados = this.transportesFiltrados;
    const seleccionados: string[] = this.transporteSeleccionado.value || [];
    return filtrados.length > 0 && filtrados.every((t) => seleccionados.includes(t));
  }

  toggleSeleccionarTodosTransportes(): void {
    const filtrados = this.transportesFiltrados;
    const seleccionados: string[] = this.transporteSeleccionado.value || [];
    if (this.todosTransportesFiltradosSeleccionados) {
      this.transporteSeleccionado.setValue(seleccionados.filter((t) => !filtrados.includes(t)));
    } else {
      this.transporteSeleccionado.setValue([...new Set([...seleccionados, ...filtrados])]);
    }
  }

  get clientesFiltrados(): ClienteItem[] {
    const q = (this.busquedaCliente.value || '').toLowerCase();
    return q
      ? this.clientes.filter(
          (c) => c.nombre.toLowerCase().includes(q) || c.codigo.toLowerCase().includes(q)
        )
      : this.clientes;
  }

  get tiposVehiculoFiltrados(): TipoVehiculoItem[] {
    const q = (this.busquedaTipoVehiculo.value || '').toLowerCase();
    if (!q) return this.tiposVehiculo;
    return this.tiposVehiculo
      .map((tipo) => {
        const nombreCoincide = tipo.nombre.toLowerCase().includes(q);
        const valores = nombreCoincide
          ? tipo.valores
          : tipo.valores.filter((v) => v.custom3.toLowerCase().includes(q));
        return { ...tipo, valores };
      })
      .filter((tipo) => tipo.valores.length > 0);
  }

  resolverNombreCliente(codigo: string | null): string {
    if (!codigo) return '-';
    if (/^\d+$/.test(codigo.trim())) return '-';
    const match = this.clientes.find(
      (c) =>
        c.codigo.toLowerCase() === codigo.toLowerCase() ||
        c.nombre.toLowerCase() === codigo.toLowerCase()
    );
    return match ? match.nombre : codigo;
  }

  render(col: { name: keyof OpItem; title: string }, row: OpItem): string {
    const val = row[col.name];
    if (val === null || val === undefined || val === '') return '-';
    if (col.name === 'FECHA_PICKING') return this.formatearFecha(String(val));
    if (col.name === 'CLIENTE') return this.resolverNombreCliente(String(val));
    return String(val);
  }

  async enviarOpsManual(): Promise<void> {
    this.helpers.notifications.warningDialog(
      'Enviar OPs Manual',
      '¿Deseas iniciar el proceso de envío de OPs a Ditransa? El proceso correrá en segundo plano.',
      'Enviar',
      async () => {
        showSpinner('Iniciando proceso de envío...');
        const result = await this.service.enviarOpsManual();
        hideSpinner();
        if (result.success) {
          this.helpers.notifications.successMessage(result.message || 'Proceso en segundo plano iniciado con éxito.');
        } else {
          this.helpers.notifications.errorMessage(result.message);
        }
      },
      '480px'
    );
  }

  limpiarFiltros(): void {
    this.busqueda.setValue('');
    this.transporteSeleccionado.setValue([]);
    this.clienteSeleccionado.setValue('');
    this.tipoVehiculoSeleccionado.setValue('');
    this.busquedaTransporte.setValue('');
    this.busquedaCliente.setValue('');
    this.busquedaTipoVehiculo.setValue('');
  }

  // ─── Histórico de envío ───────────────────────────────────────────────────
  private restarDias(fecha: Date, dias: number): Date {
    const resultado = new Date(fecha);
    resultado.setDate(resultado.getDate() - dias);
    return resultado;
  }

  private sumarDias(fecha: Date, dias: number): Date {
    const resultado = new Date(fecha);
    resultado.setDate(resultado.getDate() + dias);
    return resultado;
  }

  get fechaFinRangoMax(): Date | null {
    const inicio: Date = this.fechaInicioRangoHistorico.value;
    return inicio ? this.sumarDias(inicio, 6) : null;
  }

  private formatFechaApi(fecha: Date): string {
    const y = fecha.getFullYear();
    const m = String(fecha.getMonth() + 1).padStart(2, '0');
    const d = String(fecha.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  get rangoHistoricoExcedeLimite(): boolean {
    const inicio: Date = this.fechaInicioRangoHistorico.value;
    const fin: Date = this.fechaFinRangoHistorico.value;
    if (!inicio || !fin) return false;
    const diffDias = Math.round((fin.getTime() - inicio.getTime()) / (1000 * 60 * 60 * 24));
    return diffDias < 0 || diffDias > 6;
  }

  async buscarHistoricoEnvio(): Promise<void> {
    const esRango = this.modoFechaHistorico.value === 'rango';

    if (esRango && this.rangoHistoricoExcedeLimite) {
      this.helpers.notifications.warningMessage('El rango de fechas no puede superar los 7 días.');
      return;
    }

    let fechaInicio: string;
    let fechaFin = '';

    if (esRango) {
      fechaInicio = this.formatFechaApi(this.fechaInicioRangoHistorico.value);
      fechaFin = this.formatFechaApi(this.fechaFinRangoHistorico.value);
    } else {
      fechaInicio = this.formatFechaApi(this.fechaEspecificaHistorico.value);
    }

    this.cargandoHistorico = true;
    this.cargandoResumenHistorico = true;
    this.errorHistorico = '';
    this.historicoConsultado = true;
    this.resumenHistoricoGrupos = [];

    const [resultLista, resultResumen] = await Promise.all([
      this.service.getHistoricoEnvio(fechaInicio, fechaFin, this.filtroReintentoHistorico.value),
      this.service.getHistoricoEnvioResumen(fechaInicio, fechaFin),
    ]);

    this.cargandoHistorico = false;
    this.cargandoResumenHistorico = false;

    if (resultLista.success) {
      this.historicoDataSource.data = resultLista.data;
    } else {
      this.errorHistorico = resultLista.message || 'No se pudo cargar el histórico de envío.';
      this.historicoDataSource.data = [];
    }

    this.resumenHistoricoGrupos = resultResumen.success ? this.parsearResumenGrupos(resultResumen.data) : [];
  }

  private parsearResumenGrupos(grupos: HistoricoEnvioResumenGrupo[]): ResumenGrupoParsed[] {
    return (grupos || []).map((grupo) => {
      let success = false;
      let status = '';
      let added: string[] = [];
      let edited: string[] = [];
      let skipped: string[] = [];
      let parseError = false;

      try {
        const parsed = JSON.parse(grupo.response_json);
        success = !!parsed.success;
        status = parsed.status || '';
        added = parsed.response?.added || [];
        edited = parsed.response?.edited || [];
        skipped = parsed.response?.skipped || [];
      } catch {
        parseError = true;
      }

      return {
        fechaEnvio: grupo.fecha_envio,
        url: grupo.url,
        statusCode: grupo.status_code,
        repeticiones: grupo.repeticiones,
        success,
        status,
        added,
        edited,
        skipped,
        parseError,
      };
    });
  }

  limpiarFiltrosHistorico(): void {
    this.modoFechaHistorico.setValue('especifica');
    this.fechaEspecificaHistorico.setValue(new Date());
    this.fechaInicioRangoHistorico.setValue(this.restarDias(new Date(), 6));
    this.fechaFinRangoHistorico.setValue(new Date());
    this.busquedaHistorico.setValue('');
    this.filtroReintentoHistorico.setValue('sin_reintento');
    this.historicoDataSource.data = [];
    this.resumenHistoricoGrupos = [];
    this.errorHistorico = '';
    this.historicoConsultado = false;
  }

  exportarHistoricoExcel(): void {
    const datos = this.historicoDataSource.filteredData;
    if (!datos.length) {
      this.helpers.notifications.warningMessage('No hay registros para exportar.');
      return;
    }

    import('xlsx').then((XLSX) => {
      const datosTransformados = datos.map((row) => ({
        'Pedido SEUS': row.NUM_PEDIDO_SEUS,
        'Entrega SAP': row.NUM_ENTREGA_SAP,
        'Categoría': row.CATEGORY,
        'Detalle': row.DETALLE,
        'Estado': row.STATUS,
        'Reintento': row.REINTENTO === 'S' ? 'Sí' : 'No',
        'Código': row.STATUS_CODE,
        'Fecha Envío': row.FECHA_ENVIO,
        'Usuario': row.USUARIO_CREACION,
      }));

      const worksheet = XLSX.utils.json_to_sheet(datosTransformados);
      worksheet['!cols'] = [
        { wch: 22 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 12 }, { wch: 10 }, { wch: 20 }, { wch: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Historico Envio');

      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `historico_envio_${this.formatFechaApi(new Date())}.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    });
  }

  verDetalleHistorico(row: HistoricoEnvioItem): void {
    this.dialog.open(ModalHistoricoEnvioDetalleComponent, {
      width: '720px',
      maxWidth: '95vw',
      data: { id: row.ID },
    });
  }
}
